// Runtime loader for structure and tags thesaurus JSONs with small helpers
// NOTE: This loader is internal to the decoder. It preloads JSON artifacts for all supported versions
// and exposes indices to support generic parsing (e.g., BlockRepeating groups) without hard-coded templates.

/*
JSON shapes (generated by scripts/build-*.ts):

- Structure thesaurus (`thesaurus_struct_*.json`) is an array of components:
  interface ThEntry { componentId: string; tagText: string; indent: string; position: string; reqd: string; description: string }
  interface ThComponent { componentId: string; componentType: string; categoryId: string; name: string; abbrName: string; notReqXml: string; description: string; entries: ThEntry[] }

- Tags thesaurus (`thesaurus_*.json`) is an array of tags:
  interface ThTag { tag: string; name: string; type: string; description: string; enum?: any[] }
*/

// Import all structure JSONs (static imports for bundlers and tests)
import STRUCT_FIX_4_0 from "../thesaurus/thesaurus_struct_FIX_4_0.json";
import STRUCT_FIX_4_1 from "../thesaurus/thesaurus_struct_FIX_4_1.json";
import STRUCT_FIX_4_2 from "../thesaurus/thesaurus_struct_FIX_4_2.json";
import STRUCT_FIX_4_3 from "../thesaurus/thesaurus_struct_FIX_4_3.json";
import STRUCT_FIX_4_4 from "../thesaurus/thesaurus_struct_FIX_4_4.json";
import STRUCT_FIX_5_0 from "../thesaurus/thesaurus_struct_FIX_5_0.json";
import STRUCT_FIX_5_0SP1 from "../thesaurus/thesaurus_struct_FIX_5_0SP1.json";
import STRUCT_FIX_5_0SP2 from "../thesaurus/thesaurus_struct_FIX_5_0SP2.json";
import STRUCT_FIXT_1_1 from "../thesaurus/thesaurus_struct_FIXT_1_1.json";

// Import all tags JSONs
import TAGS_FIX_4_0 from "../thesaurus/thesaurus_FIX_4_0.json";
import TAGS_FIX_4_1 from "../thesaurus/thesaurus_FIX_4_1.json";
import TAGS_FIX_4_2 from "../thesaurus/thesaurus_FIX_4_2.json";
import TAGS_FIX_4_3 from "../thesaurus/thesaurus_FIX_4_3.json";
import TAGS_FIX_4_4 from "../thesaurus/thesaurus_FIX_4_4.json";
import TAGS_FIX_5_0 from "../thesaurus/thesaurus_FIX_5_0.json";
import TAGS_FIX_5_0SP1 from "../thesaurus/thesaurus_FIX_5_0SP1.json";
import TAGS_FIX_5_0SP2 from "../thesaurus/thesaurus_FIX_5_0SP2.json";
import TAGS_FIXT_1_1 from "../thesaurus/thesaurus_FIXT_1_1.json";
import type { FixTag } from "./thesaurus";

export interface ThEntry {
  componentId: string;
  tagText: string; // numeric tag id as string or component name
  indent: string; // "0" for top-level in a component, "1" for nested entry in a group, etc.
  position: string;
  reqd: string; // "0" | "1"
  description: string;
}

export interface ThComponent {
  componentId: string;
  componentType: string; // Block | BlockRepeating | ImplicitBlock | ...
  categoryId: string;
  name: string; // e.g., StandardHeader, Parties
  abbrName: string;
  notReqXml: string;
  description: string;
  entries: ThEntry[];
}

export type ThComponents = ThComponent[];

export interface GroupDef {
  name: string;
  countTag: number;
  delimTag: number; // first field of an entry
  entryFieldTags: number[]; // list of numeric tags within one group entry (indent=1), in declared order
}

export interface ImplicitGroupDef {
  name: string;
  countTag?: number; // optional NumInGroup-like field when present at indent=0
  delimTag: number; // entry starting tag (first indent=1 numeric)
  entryFieldTags: number[]; // numeric tags that belong to a single entry (indent=1)
}

type VersionKey =
  | "FIX_4_0"
  | "FIX_4_1"
  | "FIX_4_2"
  | "FIX_4_3"
  | "FIX_4_4"
  | "FIX_5_0"
  | "FIX_5_0SP1"
  | "FIX_5_0SP2"
  | "FIXT_1_1";

const STRUCT_BY_VERSION: Record<VersionKey, ThComponents> = {
  FIX_4_0: STRUCT_FIX_4_0 as ThComponents,
  FIX_4_1: STRUCT_FIX_4_1 as ThComponents,
  FIX_4_2: STRUCT_FIX_4_2 as ThComponents,
  FIX_4_3: STRUCT_FIX_4_3 as ThComponents,
  FIX_4_4: STRUCT_FIX_4_4 as ThComponents,
  FIX_5_0: STRUCT_FIX_5_0 as ThComponents,
  FIX_5_0SP1: STRUCT_FIX_5_0SP1 as ThComponents,
  FIX_5_0SP2: STRUCT_FIX_5_0SP2 as ThComponents,
  FIXT_1_1: STRUCT_FIXT_1_1 as ThComponents,
};

const TAGS_BY_VERSION: Record<VersionKey, { tag: string }[]> = {
  FIX_4_0: TAGS_FIX_4_0 as FixTag[],
  FIX_4_1: TAGS_FIX_4_1 as FixTag[],
  FIX_4_2: TAGS_FIX_4_2 as FixTag[],
  FIX_4_3: TAGS_FIX_4_3 as FixTag[],
  FIX_4_4: TAGS_FIX_4_4 as FixTag[],
  FIX_5_0: TAGS_FIX_5_0 as FixTag[],
  FIX_5_0SP1: TAGS_FIX_5_0SP1 as FixTag[],
  FIX_5_0SP2: TAGS_FIX_5_0SP2 as FixTag[],
  FIXT_1_1: TAGS_FIXT_1_1 as FixTag[],
};

// Cache derived data per version
const GROUPS_BY_VERSION = new Map<VersionKey, Map<number, GroupDef>>();
const IMPLICIT_GROUPS_BY_COUNT = new Map<
  VersionKey,
  Map<number, ImplicitGroupDef>
>();
const IMPLICIT_GROUPS_BY_DELIM = new Map<
  VersionKey,
  Map<number, ImplicitGroupDef>
>();
const KNOWN_TAGS_BY_VERSION = new Map<VersionKey, Set<number>>();
const COMPONENTS_BY_NAME = new Map<VersionKey, Map<string, ThComponent>>();

export function normalizeVersionKey(version: string): VersionKey | null {
  if (!version) return null;
  // Accept forms like "FIX.4.4" or "FIXT.1.1+FIX.5.0SP2"
  const upper = version.trim().toUpperCase();
  let key: string = upper;
  const plusIdx = upper.indexOf("+");
  if (plusIdx !== -1) {
    // For FIXT.1.1+FIX.5.0SP2 → we prefer the application dictionary for structure parsing
    key = upper.substring(plusIdx + 1); // e.g., FIX.5.0SP2
  }
  key = key.replace(/\./g, "_"); // e.g., FIX_4_4 or FIXT_1_1
  // Type cast guarded by explicit union above
  if (Object.hasOwn(STRUCT_BY_VERSION, key)) return key as VersionKey;
  return null;
}

export function getComponents(versionKey: VersionKey): ThComponents {
  return STRUCT_BY_VERSION[versionKey];
}

export function getComponentsByName(
  versionKey: VersionKey,
): Map<string, ThComponent> {
  let map = COMPONENTS_BY_NAME.get(versionKey);
  if (!map) {
    map = new Map<string, ThComponent>();
    for (const c of getComponents(versionKey)) {
      map.set(c.name, c);
    }
    COMPONENTS_BY_NAME.set(versionKey, map);
  }
  return map;
}

export function getKnownTagSet(versionKey: VersionKey): Set<number> {
  let s = KNOWN_TAGS_BY_VERSION.get(versionKey);
  if (!s) {
    s = new Set<number>();
    const arr = TAGS_BY_VERSION[versionKey] || [];
    for (const t of arr) {
      const n = Number(t.tag);
      if (Number.isFinite(n)) s.add(n);
    }
    KNOWN_TAGS_BY_VERSION.set(versionKey, s);
  }
  return s;
}

export function getGroupsByCountTag(
  versionKey: VersionKey,
): Map<number, GroupDef> {
  let m = GROUPS_BY_VERSION.get(versionKey);
  if (m) return m;
  m = new Map<number, GroupDef>();
  for (const comp of getComponents(versionKey)) {
    if (comp.componentType !== "BlockRepeating") continue;
    // Derive countTag (indent 0) and delimTag (first indent 1)
    let countTag: number | null = null;
    let delimTag: number | null = null;
    const entryFieldTags: number[] = [];
    for (const e of comp.entries) {
      const isNumeric = /^\d+$/.test(e.tagText);
      if (!isNumeric) continue; // skip component name references (e.g., nested groups)
      const tagNum = parseInt(e.tagText, 10);
      if (e.indent === "0") {
        // The top entry in a BlockRepeating is the NumInGroup counter
        if (countTag == null) countTag = tagNum;
      } else if (e.indent === "1") {
        if (delimTag == null) delimTag = tagNum; // first indent=1 becomes delimiter
        entryFieldTags.push(tagNum);
      }
    }
    if (countTag != null && delimTag != null) {
      m.set(countTag, {
        name: comp.name,
        countTag,
        delimTag,
        entryFieldTags,
      });
    }
  }
  GROUPS_BY_VERSION.set(versionKey, m);
  return m;
}

export function getImplicitGroupsByDelimTag(
  versionKey: VersionKey,
): Map<number, ImplicitGroupDef> {
  let byDelim = IMPLICIT_GROUPS_BY_DELIM.get(versionKey);
  let byCount = IMPLICIT_GROUPS_BY_COUNT.get(versionKey);
  if (byDelim && byCount) return byDelim;
  byDelim = new Map<number, ImplicitGroupDef>();
  byCount = new Map<number, ImplicitGroupDef>();

  for (const comp of getComponents(versionKey)) {
    if (
      comp.componentType !== "ImplicitBlockRepeating" &&
      comp.componentType !== "OptimisedImplicitBlockRepeating"
    )
      continue;

    let countTag: number | undefined = undefined;
    let delimTag: number | null = null;
    const entryFieldTags: number[] = [];
    for (const e of comp.entries) {
      const isNumeric = /^\d+$/.test(e.tagText);
      if (!isNumeric) continue;
      const tagNum = parseInt(e.tagText, 10);
      if (e.indent === "0") {
        if (countTag == null) countTag = tagNum;
      } else if (e.indent === "1") {
        if (delimTag == null) delimTag = tagNum;
        entryFieldTags.push(tagNum);
      }
    }
    if (delimTag != null) {
      const def: ImplicitGroupDef = {
        name: comp.name,
        countTag,
        delimTag,
        entryFieldTags,
      };
      byDelim.set(delimTag, def);
      if (typeof countTag === "number") byCount.set(countTag, def);
    }
  }

  IMPLICIT_GROUPS_BY_DELIM.set(versionKey, byDelim);
  IMPLICIT_GROUPS_BY_COUNT.set(versionKey, byCount);
  return byDelim;
}

export function getImplicitGroupsByCountTag(
  versionKey: VersionKey,
): Map<number, ImplicitGroupDef> {
  let byCount = IMPLICIT_GROUPS_BY_COUNT.get(versionKey);
  if (!byCount) {
    // Ensure both maps are built
    getImplicitGroupsByDelimTag(versionKey);
    byCount =
      IMPLICIT_GROUPS_BY_COUNT.get(versionKey) ||
      new Map<number, ImplicitGroupDef>();
  }
  return byCount;
}
